import {
  IApprovalWorkflow,
  IApprovalWorkflowStep,
} from "@/core/interfaces/approvalWorkflow.interface";

/** API returns `approval_steps`; create/update payload still uses `steps`. */
const STEP_KEYS = [
  "approval_steps",
  "steps",
  "workflow_steps",
  "approval_workflow_steps",
] as const;

const isWorkflowLike = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return Boolean(record.public_id || record.name || record.booking_type);
};

export const unwrapApprovalWorkflowPayload = (
  payload: unknown,
): IApprovalWorkflow | null => {
  if (!payload || typeof payload !== "object") return null;

  if (Array.isArray(payload)) return null;

  if (isWorkflowLike(payload)) {
    return payload as IApprovalWorkflow;
  }

  const record = payload as Record<string, unknown>;

  if ("response" in record) {
    const fromResponse = unwrapApprovalWorkflowPayload(record.response);
    if (fromResponse) return fromResponse;
  }

  if ("data" in record) {
    const fromData = unwrapApprovalWorkflowPayload(record.data);
    if (fromData) return fromData;
  }

  return null;
};

export const extractApprovalWorkflowSteps = (
  workflow: IApprovalWorkflow | Record<string, unknown> | null | undefined,
): IApprovalWorkflowStep[] => {
  if (!workflow || typeof workflow !== "object") return [];

  const record = workflow as Record<string, unknown>;

  for (const key of STEP_KEYS) {
    const value = record[key];
    if (Array.isArray(value) && value.length > 0) {
      return value as IApprovalWorkflowStep[];
    }
  }

  if ("response" in record) {
    const nested = extractApprovalWorkflowSteps(
      record.response as Record<string, unknown>,
    );
    if (nested.length) return nested;
  }

  if ("data" in record) {
    const nested = extractApprovalWorkflowSteps(
      record.data as Record<string, unknown>,
    );
    if (nested.length) return nested;
  }

  return [];
};
